(() => {
  'use strict';
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const state = { raf: 0, revealRaf: 0, y: scrollY, lastY: scrollY };
  const revealQueue = new Set();

  const flushReveals = () => {
    state.revealRaf = 0;
    const viewportBottom = innerHeight * .92;
    revealQueue.forEach(el => {
      if (!el.isConnected) return revealQueue.delete(el);
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportBottom && rect.bottom > 0) {
        el.classList.add('is-revealed');
        revealQueue.delete(el);
      }
    });
  };

  const queueRevealCheck = () => {
    if (!state.revealRaf) state.revealRaf = requestAnimationFrame(flushReveals);
  };

  const reveal = (scope = document) => {
    const items = [...scope.querySelectorAll('[data-reveal], [data-image-reveal]')];
    if (!items.length) return;
    if (reduced.matches || !('IntersectionObserver' in window)) return items.forEach(el => el.classList.add('is-revealed'));
    const io = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        revealQueue.delete(entry.target);
        io.unobserve(entry.target);
      }
    }), { rootMargin: '0px 0px -10%', threshold: .08 });
    items.forEach(el => { revealQueue.add(el); io.observe(el); });
    queueRevealCheck();
  };

  const splitWords = (scope = document) => {
    if (reduced.matches) return;
    scope.querySelectorAll('[data-reveal="words"]:not([data-xm-split])').forEach(el => {
      const text = el.textContent.trim();
      if (!text) return;
      el.setAttribute('aria-label', text);
      el.innerHTML = text.split(/\s+/).map((word, i) => `<span class="xm-word" aria-hidden="true" style="--xm-i:${i}"><span>${word}</span></span>`).join(' ');
      el.dataset.xmSplit = 'true';
    });
  };

  const opening = () => {
    const el = document.querySelector('.xifun-opening');
    if (!el) return;
    let seen = false;
    try { seen = sessionStorage.getItem('xifun-opening-seen') === '1'; sessionStorage.setItem('xifun-opening-seen', '1'); } catch (_) {}
    if (seen) el.classList.add('is-short');
    el.addEventListener('animationend', () => el.remove(), { once: true });
  };

  const header = () => {
    const el = document.querySelector('.header-wrapper, .header');
    if (!el) return;
    const update = () => {
      const y = scrollY;
      el.classList.toggle('xm-header-scrolled', y > 24);
      el.classList.toggle('xm-header-hidden', y > state.lastY && y > 180);
      if (Math.abs(y - state.lastY) > 6) state.lastY = y;
    };
    addEventListener('scroll', update, { passive: true }); update();
  };

  const cursor = () => {
    const el = document.querySelector('.xifun-cursor');
    if (!el || !fine.matches || reduced.matches) return;
    document.body.classList.add('xm-cursor-enabled');
    const label = el.querySelector('span'); let x = -100, y = -100;
    addEventListener('pointermove', e => { x = e.clientX; y = e.clientY; el.style.transform = `translate3d(${x - el.offsetWidth/2}px,${y - el.offsetHeight/2}px,0)`; }, { passive: true });
    document.addEventListener('pointerover', e => {
      const target = e.target.closest('[data-cursor], a, button');
      const value = target?.dataset.cursor || (target?.closest('.xm-product, .product-item') ? 'VIEW' : '');
      el.classList.toggle('is-active', !!value); label.textContent = value;
    });
  };

  const magnetic = (scope = document) => {
    if (!fine.matches || reduced.matches) return;
    scope.querySelectorAll('[data-magnetic]:not([data-xm-magnetic]), .template-product .product-form__submit:not([data-xm-magnetic])').forEach(el => {
      el.dataset.xmMagnetic = 'true';
      el.addEventListener('pointermove', e => { const r = el.getBoundingClientRect(); el.style.transform = `translate(${(e.clientX-r.left-r.width/2)*.14}px,${(e.clientY-r.top-r.height/2)*.14}px)`; });
      el.addEventListener('pointerleave', () => { el.style.transform = ''; });
    });
  };

  const cartFeedback = () => {
    const cart = document.querySelector('#halo-cart-sidebar'); if (!cart || !('MutationObserver' in window)) return;
    const observer = new MutationObserver(() => {
      const total = cart.querySelector('.cart-total-value, [data-cart-total], .price'); if (!total) return;
      total.classList.remove('xm-cart-total-updated'); requestAnimationFrame(() => total.classList.add('xm-cart-total-updated'));
    });
    observer.observe(cart, { childList:true, subtree:true, characterData:true });
  };

  const accessibleDrawers = () => {
    const menu = document.querySelector('#navigation-mobile'); if (!menu) return;
    const sync = () => { menu.inert = !document.body.classList.contains('menu_open'); };
    new MutationObserver(sync).observe(document.body, { attributes:true, attributeFilter:['class'] }); sync();
  };

  const heroFallbackVideo = (scope = document) => {
    scope.querySelectorAll('[data-xm-hero-fallback]:not([data-xm-video-ready])').forEach(video => {
      video.dataset.xmVideoReady = 'true';
      const mobile = matchMedia('(max-width: 749px)').matches;
      video.poster = mobile ? video.dataset.mobilePoster : video.dataset.desktopPoster;
      if (reduced.matches) return;
      video.src = mobile ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      video.autoplay = true;
      video.play().catch(() => {});
    });
  };

  const interactiveVideos = (scope = document) => {
    scope.querySelectorAll('[data-xm-video-frame]:not([data-xm-video-ready])').forEach(frame => {
      const video = frame.querySelector('[data-xm-brand-video]');
      const play = frame.querySelector('[data-xm-video-play]');
      if (!video || !play) return;
      frame.dataset.xmVideoReady = 'true';
      play.addEventListener('click', () => {
        frame.classList.add('is-playing');
        video.play().catch(() => frame.classList.remove('is-playing'));
      });
      video.addEventListener('pause', () => { if (!video.ended) frame.classList.remove('is-playing'); });
    });
  };

  const scrollMotion = () => {
    if (reduced.matches) return;
    const tick = () => {
      state.raf = 0; const vh = innerHeight;
      if (fine.matches) document.querySelectorAll('[data-parallax]').forEach(el => { const r=el.getBoundingClientRect(); if(r.bottom>0&&r.top<vh){ const speed=Math.max(-.3,Math.min(.3,parseFloat(el.dataset.parallax)||.12)); el.style.transform=`translate3d(0,${(r.top-vh/2)*speed}px,0)`; } });
      if (innerWidth >= 990) document.querySelectorAll('[data-horizontal]').forEach(section => { const stage=section.querySelector('.horizontal-showcase__stage'); const track=section.querySelector('.horizontal-showcase__track'); if(!stage||!track)return; const r=stage.getBoundingClientRect(); const max=Math.max(0,track.scrollWidth-innerWidth); const progress=Math.max(0,Math.min(1,-r.top/Math.max(1,r.height-vh))); section.style.setProperty('--xm-track', `${-max*progress}px`); });
    };
    addEventListener('scroll', () => { if(!state.raf) state.raf=requestAnimationFrame(tick); }, {passive:true}); addEventListener('resize', tick, {passive:true}); tick();
  };

  const transitions = () => {
    const overlay = document.querySelector('.xifun-page-transition'); if (!overlay || reduced.matches) return;
    document.addEventListener('click', e => { const a=e.target.closest('a[href]'); if(!a||e.defaultPrevented||e.button!==0||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey||a.target||a.hasAttribute('download'))return; const url=new URL(a.href,location.href); if(url.origin!==location.origin||url.hash||url.pathname.includes('/checkout'))return; e.preventDefault(); overlay.classList.add('is-active'); setTimeout(()=>location.href=url.href,360); });
    addEventListener('pageshow', () => overlay.classList.remove('is-active'));
  };

  const init = (scope = document) => {
    splitWords(scope); reveal(scope); magnetic(scope); heroFallbackVideo(scope); interactiveVideos(scope);
    scope.querySelectorAll('#navigation-mobile .menu-lv-1').forEach((item, index) => item.style.setProperty('--xm-menu-i', index));
  };
  addEventListener('scroll', queueRevealCheck, { passive: true });
  addEventListener('resize', queueRevealCheck, { passive: true });
  root.classList.add('motion-ready'); opening(); init(); header(); cursor(); scrollMotion(); transitions(); cartFeedback(); accessibleDrawers();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
